import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import CustomTable from '../../../components/atoms/CustomTable';
import TitleText from '../../../components/atoms/TitleText';
import {
  PromotionReportRow,
  PromotionSlotUsage,
  PromotionDashboardRow,
} from '../../../types/promotionsReports';
import {
  useGetPromotionsDashboardQuery,
  useGetPromotionsStatisticsQuery,
  useLazyGetPromotionsDashboardDayQuery,
} from '../../../store/services/promotionsService';
import HomeStatCard from '../../../components/atoms/HomeStatCard';
import ApplicationDetailsModal from '../../AdminConfigurations/promotionsRequests/ApplicationDetailsModal';

const getTypeTranslation = (code: string) => {
  switch (code) {
    case 'HERO_BANNER':
      return 'home.hero-banners';
    case 'TOP_PRODUCTS':
      return 'home.top-products';
    case 'FLASH_DEALS':
      return 'home.flash-deals';
    default:
      return code;
  }
};

const getPlacementTranslation = (code: string) => {
  switch (code) {
    case 'MAIN_MARKETPLACE':
      return 'home.main-marketplace';
    case 'CATEGORY_MARKETPLACE':
      return 'home.category-page';
    default:
      return code;
  }
};

export default function PromotionsReportsPage() {
  const { t, i18n } = useTranslation();
  const [startDate] = useState(new Date());

  const [selectedItem, setSelectedItem] = useState<{ id: string } | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [promotionsList, setPromotionsList] = useState<{ id: string }[]>([]);

  const [fetchDayDetails, { isFetching: isFetchingDayDetails }] = useLazyGetPromotionsDashboardDayQuery();

  const endDate = dayjs(startDate).add(13, 'day');

  const { data: dashboardResponse, isFetching } =
    useGetPromotionsDashboardQuery({
      fromDate: dayjs(startDate).format('YYYY-MM-DD'),
      toDate: endDate.format('YYYY-MM-DD'),
    });

  const { data: statsResponse } = useGetPromotionsStatisticsQuery();
  const stats = statsResponse?.payload;

  const data = useMemo(() => {
    // Some backend APIs return the array directly, others wrap in payload
    const payload = Array.isArray(dashboardResponse)
      ? dashboardResponse
      : dashboardResponse?.payload || [];

    return payload.map((item: PromotionDashboardRow, index: number) => {
      const row: PromotionReportRow = {
        id: String(index),
        type: getTypeTranslation(item.promotionTypeCode),
        placement: getPlacementTranslation(item.marketplaceScopeCode),
        marketplaceScopeCode: item.marketplaceScopeCode,
        promotionTypeCode: item.promotionTypeCode,
      };

      item.days.forEach((day) => {
        row[day.dayDate] = {
          used: day.bookedCount,
          total: day.slotNo,
          full: day.full,
        };
      });

      return row;
    });
  }, [dashboardResponse]);

  const handleCellClick = async (record: PromotionReportRow, dateKey: string, value: PromotionSlotUsage | undefined) => {
    if (!value || value.used === 0) return;
    if (!record.marketplaceScopeCode || !record.promotionTypeCode) return;

    try {
      const res = await fetchDayDetails({
        marketplaceScopeCode: record.marketplaceScopeCode,
        promotionTypeCode: record.promotionTypeCode,
        day: dateKey,
      }).unwrap();

      const bookings = res?.payload?.bookings || [];
      if (bookings.length > 0) {
        const list = bookings.map((b) => ({ id: b.promotionRequestId }));
        setPromotionsList(list);
        setSelectedItem(list[0]);
        setSelectedIndex(0);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handlePrevious = () => {
    if (selectedIndex > 0) {
      const prevIndex = selectedIndex - 1;
      setSelectedItem(promotionsList[prevIndex]);
      setSelectedIndex(prevIndex);
    }
  };

  const handleNext = () => {
    if (selectedIndex < promotionsList.length - 1) {
      const nextIndex = selectedIndex + 1;
      setSelectedItem(promotionsList[nextIndex]);
      setSelectedIndex(nextIndex);
    }
  };

  const columns = useMemo(() => {
    // 1. The fixed leftmost column
    const cols: any[] = [
      {
        title: t('home.type-placement'),
        dataIndex: 'typePlacement',
        key: 'typePlacement',
        width: 250,
        fixed: 'left',
        onCell: () => ({
          style: {
            position: 'sticky',
            left: 0,
            backgroundColor: '#ffffff',
            zIndex: 50,
          },
        }),
        onHeaderCell: () => ({
          style: {
            position: 'sticky',
            left: 0,
            backgroundColor: '#ffffff',
            zIndex: 51,
          },
        }),
        render: (_: any, record: PromotionReportRow) => (
          <div className="flex flex-col bg-white relative z-[60] w-full h-full">
            <span className="text-sm font-semibold text-gray-900">
              {t(record.type)}
            </span>
            <span className="text-xs font-normal text-gray-500">
              {t(record.placement)}
            </span>
          </div>
        ),
      },
    ];

    // 2. The 14 day columns
    for (let i = 0; i < 14; i++) {
      const date = dayjs(startDate).add(i, 'day');
      const dateKey = date.format('YYYY-MM-DD');

      // Use localized format for date header
      const dateFormatted = date.locale(i18n.language).format('D MMM');

      cols.push({
        title: (
          <div className="flex flex-col items-center">
            <span className="text-sm font-medium">
              {dateFormatted.split(' ')[0]}
            </span>
            <span className="text-sm font-medium">
              {dateFormatted.split(' ')[1]}
            </span>
          </div>
        ),
        dataIndex: dateKey,
        key: dateKey,
        width: 80,
        align: 'center',
        render: (value: PromotionSlotUsage | undefined, record: PromotionReportRow) => {
          if (!value || value.used === 0) return null;

          const isFull = value.full ?? value.used === value.total;
          const bgClass = isFull ? 'bg-[#4DA167]' : 'bg-[#F58F29]'; // Green if full, Orange if partial

          // Hide total if slotNo is infinity/very large like 777777777
          const isInfinite = value.total > 1000000;

          return (
            <div
              className={`inline-flex items-center justify-center p-3.5 rounded-md text-white text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity ${bgClass} ${isFetchingDayDetails ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => handleCellClick(record, dateKey, value)}
            >
              {isInfinite ? value.used : `${value.used}/${value.total}`}
            </div>
          );
        },
      });
    }

    return cols;
  }, [startDate, t, i18n.language]);

  const dateRangeLabel = `${dayjs(startDate).locale(i18n.language).format('MMM DD, YYYY')} - ${endDate.locale(i18n.language).format('MMM DD, YYYY')}`;

  return (
    <div
      className="w-full flex flex-col gap-8"
      data-test="promotions-reports-page"
    >
      {/* Top Header & Statistics */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <TitleText
            text={t('home.promotions-reports', 'Promotions')}
            className="!font-bold !text-3xl !leading-9 text-[#111928]"
          />
          <p className="text-[#637381] text-base">
            {t('home.promotions-reports-desc', 'Configure slot limits and duration pricing, then review seller requests.')}
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <HomeStatCard
              title={t('home.promo-total-requests', 'Total requests')}
              number={stats?.totalRequests ?? 0}
            />
            <HomeStatCard title={t('home.promo-pending-review', 'Pending review')} number={stats?.pending ?? 0} />
            <HomeStatCard
              title={t('home.promo-order-value', 'Order value')}
              number={
                stats?.orderValue
                  ? `${stats.orderValue.toLocaleString('en-US', { minimumFractionDigits: 2 })} ${t('SAR', 'SAR')}`
                  : `0.00 ${t('SAR', 'SAR')}`
              }
            />
            <HomeStatCard title={t('home.promo-active-now', 'Active now')} number={stats?.active ?? 0} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <HomeStatCard title={t('home.promo-upcoming', 'Upcoming')} number={stats?.upComing ?? 0} />
            <HomeStatCard title={t('home.promo-rejected', 'Rejected')} number={stats?.rejected ?? 0} />
            <HomeStatCard
              title={t('home.promo-total-profit', 'Total profit from approved promotions')}
              number={
                stats?.totalProfit
                  ? `${stats.totalProfit.toLocaleString('en-US')} ${t('SAR', 'SAR')}`
                  : `0 ${t('SAR', 'SAR')}`
              }
            />
          </div>
        </div>
      </div>

      {/* Calendar Section */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden w-full flex flex-col">
        <div className="flex justify-between items-start p-6">
          <div className="flex flex-col gap-1">
            <TitleText
              text={t('home.promotions-reports-calendar', 'Slot calendar — next 14 days')}
              className="!font-bold !text-2xl !leading-8 text-[#111928]"
            />
            <p className="text-[#637381] text-sm">
              {t('home.promotions-reports-desc', 'Configure slot limits and duration pricing, then review seller requests.')}
            </p>
          </div>

          <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm text-sm font-medium text-gray-700">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 2V5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16 2V5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3.5 9.08984H20.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15.6947 13.7002H15.7037"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15.6947 16.7002H15.7037"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.9955 13.7002H12.0045"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.9955 16.7002H12.0045"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.29431 13.7002H8.30329"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.29431 16.7002H8.30329"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {dateRangeLabel}
          </div>
        </div>

        <style>{`
        .ant-table-cell-fix-left {
          background-color: #ffffff !important;
          z-index: 50 !important;
        }
        .promotions-table .ant-table-cell,
        .promotions-table .ant-table-thead > tr > th {
          border-inline-end: 1px solid #f0f0f0 !important;
          border-bottom: 1px solid #f0f0f0 !important;
        }
      `}</style>
        <CustomTable
          className="promotions-table"
          columns={columns}
          dataSource={data}
          loading={isFetching}
          pagination={false}
          currentPage={1}
          totalElements={data.length}
          onPageChange={() => {}}
          scroll={{ x: 1400 }}
          bordered={true}
          rowKey="id"
        />
      </div>

      <ApplicationDetailsModal
        isOpen={!!selectedItem}
        onClose={() => {
          setSelectedItem(null);
          setSelectedIndex(-1);
        }}
        item={selectedItem as any}
        onPrevious={handlePrevious}
        onNext={handleNext}
        hasPrevious={selectedIndex > 0}
        hasNext={
          selectedIndex >= 0 && selectedIndex < promotionsList.length - 1
        }
      />
    </div>
  );
}
